/*eslint max-lines-per-function: ["error", 250]*/
import React, { useCallback } from 'react';
import { Card, Tab } from 'react-bootstrap';
import { useParams } from 'react-router-dom';

import type {
  GridGetterFunction,
  GridCountFunction,
} from '../../../common/components/Grid/GridDataTypes';
import { useSpdCore } from '../../../common/hooks/useSpdCore';
import { useNavigation } from '../../../common/navigation';
import { TabsHistory } from '../../../app/components/TabsHistory';
import { SalesInvoiceSearch } from '../../components/SalesInvoice/SalesInvoicesSearch';
import { SalesInvoiceStatusEnum } from '../../../common/spdCore/validation/enums';
import { InvoicePaymentsSearch } from '../../components/InvoicePayment/InvoicePaymentsSearch';

enum CompanyInvoicesTabs {
  OVERDUE = 'overdue',
  IN_TIME = 'in-time',
  PAYMENTS = 'payments',
}

export const CompanyOverdueInvoicesView: React.FC = () => {
  const spd = useSpdCore();
  const nav = useNavigation();
  const { companyId, companyName } = useParams<{
    companyId: string;
    companyName: string;
  }>();

  const decodedCompanyName = companyName ? decodeURIComponent(companyName) : '';
  const unpaidStatuses = `${SalesInvoiceStatusEnum.Approved},${SalesInvoiceStatusEnum.InKSeF}`;

  const getDataOverdue: GridGetterFunction = useCallback(
    (
      pageSize,
      pageNumber,
      orderBy,
      orderDirection,
      filterText,
      filterColumns,
    ) =>
      spd.salesInvoices.getAllByPortion(
        pageSize,
        pageNumber,
        orderBy,
        orderDirection,
        filterText,
        filterColumns,
        undefined,
        undefined,
        true,
        unpaidStatuses,
        undefined,
        undefined,
        undefined,
        undefined,
        companyId,
        true,
      ),

    [spd.salesInvoices, companyId, unpaidStatuses],
  );

  const getCountOverdue: GridCountFunction = useCallback(
    (filterText?: string, filterColumns?: string) =>
      spd.salesInvoices.getAllCount(
        filterText,
        filterColumns,
        undefined,
        undefined,
        true,
        unpaidStatuses,
        undefined,
        undefined,
        undefined,
        undefined,
        companyId,
        true,
      ),

    [spd.salesInvoices, companyId, unpaidStatuses],
  );

  const getDataInTime: GridGetterFunction = useCallback(
    (
      pageSize,
      pageNumber,
      orderBy,
      orderDirection,
      filterText,
      filterColumns,
    ) =>
      spd.salesInvoices.getAllByPortion(
        pageSize,
        pageNumber,
        orderBy,
        orderDirection,
        filterText,
        filterColumns,
        undefined,
        undefined,
        true,
        unpaidStatuses,
        undefined,
        undefined,
        undefined,
        undefined,
        companyId,
        false,
      ),

    [spd.salesInvoices, companyId, unpaidStatuses],
  );

  const getCountInTime: GridCountFunction = useCallback(
    (filterText?: string, filterColumns?: string) =>
      spd.salesInvoices.getAllCount(
        filterText,
        filterColumns,
        undefined,
        undefined,
        true,
        unpaidStatuses,
        undefined,
        undefined,
        undefined,
        undefined,
        companyId,
        false,
      ),

    [spd.salesInvoices, companyId, unpaidStatuses],
  );

  const getDataPayments: GridGetterFunction = useCallback(
    (
      pageSize,
      pageNumber,
      orderBy,
      orderDirection,
      filterText,
      filterColumns,
    ) =>
      spd.invoicePayments.getAllByPortion(
        pageSize,
        pageNumber,
        orderBy,
        orderDirection,
        filterText,
        filterColumns,
        undefined,
        undefined,
        undefined,
        companyId,
      ),

    [spd.invoicePayments, companyId],
  );

  const getCountPayments: GridCountFunction = useCallback(
    (filterText?: string, filterColumns?: string) =>
      spd.invoicePayments.getAllCount(
        filterText,
        filterColumns,
        undefined,
        undefined,
        undefined,
        companyId,
      ),

    [spd.invoicePayments, companyId],
  );

  const handleRowClick = (id: string) => {
    nav.salesInvoiceEditor(id, true);
  };

  const handlePaymentRowClick = (id: string) => {
    nav.invoicePaymentEditor(id, true);
  };

  const [activeTab, setActiveTab] = React.useState<string>(
    CompanyInvoicesTabs.OVERDUE,
  );

  return (
    <Card className="main-card">
      <Card.Body className="m-3">
        <Card.Title className="pb-3">
          Nieopłacone faktury i wpłaty powiązane z firmą — {decodedCompanyName}
        </Card.Title>
        <TabsHistory
          activeKey={activeTab}
          setActiveTab={setActiveTab}
          id="company-invoices"
          className="mb-3"
          mountOnEnter
          unmountOnExit
        >
          <Tab
            eventKey={CompanyInvoicesTabs.OVERDUE}
            title="Nieopłacone faktury - po terminie"
            className="m-3"
          >
            <SalesInvoiceSearch
              getData={getDataOverdue}
              getCount={getCountOverdue}
              onRowClick={handleRowClick}
              withFilterInColumns={true}
              showFilter={true}
              showExport={true}
            />
          </Tab>
          <Tab
            eventKey={CompanyInvoicesTabs.IN_TIME}
            title="Nieopłacone faktury - w terminie"
            className="m-3"
          >
            <SalesInvoiceSearch
              getData={getDataInTime}
              getCount={getCountInTime}
              onRowClick={handleRowClick}
              withFilterInColumns={true}
              showFilter={true}
              showExport={true}
            />
          </Tab>
          <Tab
            eventKey={CompanyInvoicesTabs.PAYMENTS}
            title="Wpłaty dowiązane do faktur firmy"
            className="m-3"
          >
            <InvoicePaymentsSearch
              getData={getDataPayments}
              getCount={getCountPayments}
              onRowClick={handlePaymentRowClick}
              withFilterInColumns={true}
              showFilter={true}
              showExport={true}
            />
          </Tab>
        </TabsHistory>
      </Card.Body>
    </Card>
  );
};
